import { NextRequest, NextResponse } from 'next/server'
import { ANONYMOUS_ID_HEADER, SEGMENT_ANONYMOUS_ID_COOKIE } from '@/constants/cookies'
import { paths } from '@/constants/paths'
import { AnalyticsEvent } from '@/utils/analytics'
import { isProductionEnvironment } from '@/utils/environment-utils'
import { SegmentNodeTrackParams, track } from '../SegmentNode'
import { cloneNextUrlForPathname } from './MiddlewareNextUrlUtil'
import { NextRedirect } from './types'

const ANGEL_COOKIE_DOMAIN = '.angel.com'
const ANGEL_PREVIEW_COOKIE_DOMAIN = '.angel-preview.com'

export const getCookieDomain = (): string | undefined => {
  if (isProductionEnvironment()) {
    return ANGEL_COOKIE_DOMAIN
  }

  try {
    const baseUrl = paths.getBaseURL()
    const url = new URL(baseUrl)
    if (url.hostname === 'angel-preview.com' || url.hostname.endsWith('.angel-preview.com')) {
      return ANGEL_PREVIEW_COOKIE_DOMAIN
    }
  } catch {
    // If URL parsing fails, fall back to undefined
  }

  // Staging, localhost, and other environments - no domain (host-only cookies)
  return undefined
}

export async function redirectRequest(
  request: NextRequest,
  redirect: NextRedirect,
  trackingEvent: AnalyticsEvent,
  trackingPayload?: SegmentNodeTrackParams,
): Promise<NextResponse> {
  if (trackingPayload) {
    await track(trackingEvent, trackingPayload)
  }

  const isRelativeRedirect = redirect.destination.startsWith('/')
  const url = isRelativeRedirect ? cloneNextUrlForPathname(redirect.destination, request) : redirect.destination
  const status = redirect.permanent ? 308 : 307
  return NextResponse.redirect(url, { status })
}

export const extractAnonymousIdFromNextRequest = (request: NextRequest, onlyCheckCookies: boolean = false) => {
  const anonymousId = request.cookies.get(SEGMENT_ANONYMOUS_ID_COOKIE)?.value

  if (anonymousId) return anonymousId

  if (!onlyCheckCookies) {
    const headerAnonId = request.headers.get(ANONYMOUS_ID_HEADER)
    if (headerAnonId) return headerAnonId
  }

  return null
}
